import React, { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import {
  PRIMARY_BLUE,
  LIGHTEST_GREY,
  MEDIUM_GREY,
  WHITE,
  BLACK,
  BORDER_GREY,
  ERROR_RED_BG,
  ERROR_RED,
  POSITIVE_GREEN
} from 'constants/colours';

type ButtonTypes = 'primary' | 'secondary' | 'tertiary' | 'red' | 'toCheckIn' | 'checkedIn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  secondary?: boolean;
  tertiary?: boolean;
  red?: boolean;
  toCheckIn?: boolean;
  checkedIn?: boolean;
  compact?: boolean;
  short?: boolean;
  height?: string;
  minWidth?: string;
  maxWidth?: string;
  padding?: string;
  icon?: React.ReactNode;
  afterIcon?: React.ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  onClick: () => void;
  children: JSX.Element | string | string[] | false;
}

const BUTTON_TYPE_PROPERTIES: {
  [type: string]: {
    background: string;
    color: string;
    outline: string;
    border?: string;
    hoverFilter?: string;
    disabledOpacity?: string;
  };
} = {
  primary: {
    background: PRIMARY_BLUE,
    color: WHITE,
    outline: PRIMARY_BLUE,
    hoverFilter: 'brightness(90%)'
  },
  secondary: {
    background: LIGHTEST_GREY,
    color: BLACK,
    outline: MEDIUM_GREY
  },
  tertiary: {
    background: WHITE,
    color: BLACK,
    border: `1px solid ${BORDER_GREY}`,
    outline: MEDIUM_GREY,
    disabledOpacity: '0.7'
  },
  red: {
    background: ERROR_RED_BG,
    color: ERROR_RED,
    outline: ERROR_RED
  },
  toCheckIn: {
    background: WHITE,
    color: POSITIVE_GREEN,
    border: `1.5px solid ${POSITIVE_GREEN}`,
    outline: POSITIVE_GREEN
  },
  checkedIn: {
    background: POSITIVE_GREEN,
    color: WHITE,
    outline: POSITIVE_GREEN
  }
};

const ButtonWrapper = styled.button<{
  buttonType: ButtonTypes;
  $compact: boolean;
  $short?: boolean;
  minWidth?: string;
  maxWidth?: string;
  padding?: string;
  isLoading: boolean;
  height?: string;
}>(
  ({ buttonType, $compact, $short, minWidth, maxWidth, padding, isLoading, height }) => css`
    min-height: ${(): string => {
      if (height) return height;
      if ($compact) return '36px';
      if ($short) return '40px';
      return '48px';
    }};
    height: ${(): string => {
      if (height) return height;
      if ($compact) return '36px';
      if ($short) return '40px';
      return '48px';
    }};
    min-width: ${minWidth || '70px'};
    max-width: ${maxWidth};
    border: ${BUTTON_TYPE_PROPERTIES[buttonType].border || 'none'};
    background: ${BUTTON_TYPE_PROPERTIES[buttonType].background};
    color: ${BUTTON_TYPE_PROPERTIES[buttonType].color};
    padding: ${($compact || $short) && buttonType !== 'toCheckIn' && buttonType !== 'checkedIn'
      ? `4px ${padding}`
      : padding};
    margin: 1px 1px 3px 1px;
    border-radius: ${$compact ? '30px' : '4px'};
    text-align: center;
    font-weight: 500;
    font-size: 14px;
    transition: background 0.05s;
    display: flex;
    align-items: center;
    justify-content: center;
    ${!isLoading &&
    `
        &:focus {
          margin: 1px 0 3px 0;
          border: 1px solid ${WHITE};
          box-shadow: 0 0 1pt 1px ${BUTTON_TYPE_PROPERTIES[buttonType].outline};
        }
      `}
    &:hover {
      filter: ${BUTTON_TYPE_PROPERTIES[buttonType].hoverFilter || 'brightness(95%)'};
    }
    &:disabled {
      pointer-events: none;
      cursor: default;
      opacity: ${BUTTON_TYPE_PROPERTIES[buttonType].disabledOpacity || '0.4'};
    }
  `
);

const IconWrapper = styled.span`
  margin-right: 8px;
`;

const AfterIconWrapper = styled.span`
  margin-left: 8px;
`;

const Button: FunctionComponent<React.PropsWithChildren<ButtonProps>> = ({
  secondary = false,
  red = false,
  tertiary = false,
  compact = false,
  toCheckIn = false,
  checkedIn = false,
  short = false,
  minWidth,
  maxWidth,
  height,
  padding = '16px',
  icon = null,
  afterIcon = null,
  isLoading = false,
  isDisabled = false,
  onClick,
  children,
  ...forwardedProps
}) => {
  let buttonType: ButtonTypes = 'primary';
  if (secondary) {
    buttonType = 'secondary';
  } else if (tertiary) {
    buttonType = 'tertiary';
  } else if (red) {
    buttonType = 'red';
  } else if (toCheckIn) {
    buttonType = 'toCheckIn';
  } else if (checkedIn) {
    buttonType = 'checkedIn';
  }

  return (
    <ButtonWrapper
      buttonType={buttonType}
      $compact={compact}
      $short={short}
      height={height}
      minWidth={minWidth}
      maxWidth={maxWidth}
      padding={padding}
      isLoading={isLoading}
      disabled={isDisabled}
      onClick={onClick}
      {...forwardedProps}
    >
      {icon && !isLoading ? <IconWrapper>{icon}</IconWrapper> : null}
      {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : children}
      {afterIcon && !isLoading ? <AfterIconWrapper>{afterIcon}</AfterIconWrapper> : null}
    </ButtonWrapper>
  );
};

export default Button;
